import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, BoxProps, IconButton, IconButtonProps, TextField, TextFieldProps, Tooltip } from '@mui/material';
import Grid, { Grid2Props } from '@mui/material/Grid2';
import {
  AddPhotoAlternateOutlined as SetImageIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { ControlsStringAssets, MessagesStringAssets, TooltipStringAssets } from '../../../assets/stringAssets';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWithActionBar from '../FormWithActionBar/FormWithActionBar';
import { IUserSettings } from '../../../dataObjects/models/users/UserSettings';
import { normalizePhoneNumber } from '../../utilities';
import { styled } from '@mui/styles';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';
import { useNavigate } from 'react-router-dom';
import { enumDigitalMediaDisplayEnvironment, enumImageFileSize, enumPersistableObjectType } from '../../../dataObjects/enums';
import { ImageMediaDisplayForProfileImage } from '../../controls/digitalMediaDisplays/ImageMediaDisplayForProfileImage';
import { ICurrentUserContextData, useCurrentUserContext } from '../../providersAndContexts/currentUser';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/


// a styled Grid for a container that surrounds the Set Profile Image Button area
const StyledGridForSetProfileImageButtonArea = styled((props: Grid2Props) => (
  <Grid
    justifyItems={'center'}
    {...props}
  />
))(({ theme }) => ({
  // [theme.breakpoints.up('xs')]: {
  //   width: '120px',
  // },
  // [theme.breakpoints.up('sm')]: {
  //   width: '120px',
  // },
  // [theme.breakpoints.up('md')]: {
  //   width: '120px',
  // },
  // [theme.breakpoints.up('lg')]: {
  //   width: '150px',
  // },
  // [theme.breakpoints.up('xl')]: {
  //   width: '150px',
  // },
}));

// a styled Box (equivalent to a FlaxBox <div>) for a container that surrounds the Set Profile Image Button
const StyledBoxForSetProfileImageButtonContainer = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  display: 'flex',
}));

// a style Span to be used for the Set Profile Image button (appears when there is no Profile Image for the Category)
const StyledSpanForSetProfileImageButton = styled((props: BoxProps) => (
  <Box
    component='span'
    {...props}
  />
))(({ theme }) => ({
  border: theme.cardViewCard.editIconButton.border,
  borderRadius: theme.shape.borderRadius,
  background: theme.cardViewCard.editIconButton.background,
  color: theme.cardViewCard.editIconButton.iconColor,
  '&:hover': {
    border: theme.cardViewCard.editIconButton.hoverBorder,
    background: theme.cardViewCard.editIconButton.hoverBackground,
    color: theme.cardViewCard.editIconButton.hoverIconColor,
  }
}));

// a style Span to be used for the Delete Icon button beneath the Profile Image
const StyledSpanForProfileImageDeleteIconButton = styled((props: BoxProps) => (
  <Box
    component='span'
    {...props}
  />
))(({ theme }) => ({
  border: theme.cardViewCard.deleteIconButton.border,
  borderRadius: theme.shape.borderRadius,
  background: theme.cardViewCard.deleteIconButton.background,
  color: theme.cardViewCard.deleteIconButton.iconColor,
  '&:hover': {
    border: theme.cardViewCard.deleteIconButton.hoverBorder,
    background: theme.cardViewCard.deleteIconButton.hoverBackground,
    color: theme.cardViewCard.deleteIconButton.hoverIconColor,
  }
}));

// a styled Box (equivalent to a <div>), providing an area to display the overall Progress Bar Area
const StyledBoxForEmailTextContainer = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  paddingRight: theme.spacing(2),
  width: "100%",
}));

// a styled TextField that is serving as a Select control
const StyledTextFieldForSelectControl = styled((props: TextFieldProps) => (
  <TextField
    select
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

// a style IconButton to be used for the Edit Icon buttons next to the email address
const StyledIconButton = styled((props: IconButtonProps) => (
  <IconButton
    size='small'
    {...props}
  />
))(({ theme }) => ({
  border: theme.listViewTable.detailRow.editIconButton.border,
  borderRadius: theme.shape.borderRadius,
  background: theme.listViewTable.detailRow.editIconButton.background,
  color: theme.listViewTable.detailRow.editIconButton.iconColor,
  '&:hover': {
    border: theme.listViewTable.detailRow.editIconButton.hoverBorder,
    background: theme.listViewTable.detailRow.editIconButton.hoverBackground,
    color: theme.listViewTable.detailRow.editIconButton.hoverIconColor,
  }
}));

interface IUserSettingsFormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
}

// using 'yup', set up a schema for the form field values
const schema = yup.object().shape({
  firstName: yup
    .string()
    .required(ControlsStringAssets.firstNameRequired),
  lastName: yup
    .string()
    .required(ControlsStringAssets.lastNameRequired),
  email: yup
    .string()
    .email(ControlsStringAssets.emailInvalid),
  // .required(ControlsStringAssets.emailRequired),
  mobilePhone: yup
    .string()
    .matches(RegularExpressions.PhoneNumber, ControlsStringAssets.phoneNumberInvalid)
    .required(ControlsStringAssets.phoneNumberRequired),
});


/**
 * @interface IUserSettingsFormProps Properties for the UserForm component
 */
export interface IUserSettingsFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {IUserSettings} userSettings The UserSettings details for the form 
   */
  userSettings: IUserSettings,
  /**
   * @property {boolean} saveRequestInProgress Whether a save request is in progress
   */
  saveRequestInProgress?: boolean,
  /**
   * @property {(userSettings: IUserSettings) => Promise<void>} onSubmit Method to call for submitting the form for a save operation
   */
  onSubmit: (userSettings: IUserSettings) => Promise<void>,
  onDeleteProfileImage: () => void;
}

const UserSettingsForm: React.FC<IUserSettingsFormProps> = (props: IUserSettingsFormProps) => {
  UserSettingsForm.displayName = 'User Settings Form';

  // get required arguments from props
  const { userSettings, onSubmit, onDeleteProfileImage } = props;

  // set up details for ReactHookForm
  const { register, formState, formState: { errors }, handleSubmit } = useForm({
    defaultValues: {
      firstName: userSettings.firstName,
      lastName: userSettings.lastName,
      email: userSettings.email,
      mobilePhone: userSettings.mobilePhone ? normalizePhoneNumber(userSettings.mobilePhone) : '',
    },
    // mode: "onBlur",
    mode: "all",
    resolver: yupResolver(schema)
  });

  const { ref: emailReg, ...emailProps } = register("email", { required: true });
  const { ref: firstNameReg, ...firstNameProps } = register("firstName", { required: true });
  const { ref: lastNameReg, ...lastNameProps } = register("lastName", { required: true });
  const { ref: mobilePhoneReg, ...mobilePhoneProps } = register("mobilePhone", { required: true });
  // const { ref: preferredLocaleReg, ...preferredLocaleProps } = register("preferredLocale", { required: true });

  // the 'useNavigate' hook will be used to navigate the browser
  const navigate = useNavigate();

  // use a custom hook to get the userProfileImage from the CurrentUserProvider
  const { userProfileImage }: ICurrentUserContextData = useCurrentUserContext();

  // for testing whether the form is in a valid state
  const { isValid } = formState;

  const [formIsValid, setFormIsValid] = useState<boolean>(false);

  useEffect(() => {
    setFormIsValid(isValid);
  }, [isValid]);

  // capture whether a save is currently being submitted
  const saveRequestInProgress: boolean = props.saveRequestInProgress ?? false;

  // state value indicating whether a save is in progress
  const [saveInProgress, setSaveInProgress] = useState<boolean>(saveRequestInProgress);

  // useEffect hook for setting the 'saveInProgress' local state based on whether a save is currently in progress
  useEffect(() => {
    setSaveInProgress(saveRequestInProgress);
  }, [saveRequestInProgress]);


  // handles a save/submit request from the form
  // const handleSaveSubmit = async (data: IUserSettingsFormValues) => {
  const handleSaveSubmit = async (data: any) => {

    setSaveInProgress(true);

    // fill in details for the UserSettings object passed in
    userSettings.firstName = data.firstName;
    userSettings.lastName = data.lastName;
    // userSettings.email = data.email; // this is commented out, since we don't allow user to change email address
    userSettings.mobilePhone = data.mobilePhone;

    // call the onSubmit handler passed in, supplying the UserSettings object
    await onSubmit(userSettings);
  }

  // present the form
  return (
    <>
      <FormWithActionBar
        onSubmit={handleSubmit(handleSaveSubmit)}
        actionInProgress={saveInProgress}
        actionInProgressLabel={MessagesStringAssets.userSettings_SaveRequested}
        formIsValid={formIsValid}
      >

        {(userProfileImage === undefined) &&
          <StyledGridForSetProfileImageButtonArea>
            <StyledBoxForSetProfileImageButtonContainer>
              {/* <Grid direction='row' justifyItems={'center'}>
                        <Box display='flex'> */}
              <Tooltip
                // the tooltip for the edit button will either be "Set profile image" (if user has sufficient permission) or "Set profile image disallowed" (insufficient permission)
                title={TooltipStringAssets.setProfileImage}
                arrow
              >
                <StyledSpanForSetProfileImageButton
                  style={{ borderRadius: '50%', padding: '8px' }}
                >
                  <SetImageIcon
                    style={{ scale: '1.0' }}
                    fontSize='large'
                    // upon a click of this button, inform parent component to NOT perform page back notification if informed of a user save operation, and then navigate to the profileImageLink page
                    onClick={() => { navigate(`/${userSettings.id}/${enumPersistableObjectType.UserSettings}/profileImageLink`) }}
                    // onClick={() => { handleClickOnSetProfileImageButton() }}
                  />
                </StyledSpanForSetProfileImageButton>
              </Tooltip>
              {/* </Box>
                   </Grid> */}
            </StyledBoxForSetProfileImageButtonContainer>
          </StyledGridForSetProfileImageButtonArea>
        }

        {(userProfileImage !== undefined) &&
          <Grid container direction={'column'} justifySelf={'center'}>
            <Grid container justifyContent={'center'} >
              <ImageMediaDisplayForProfileImage
                imageLink={userProfileImage}
                displayEnvironment={enumDigitalMediaDisplayEnvironment.FormView}
                displayImageSize={enumImageFileSize.Small}
                displayAsRound={true}
              />
            </Grid>
            <Grid container justifyContent={'center'} >
              <Tooltip
                // the tooltip for the Delete Profile Image button will either be "Delete profile image" (if user has sufficient permission) or "Delete profile image disallowed" (insufficient permission)
                title={TooltipStringAssets.deleteProfileImage}
                arrow
              >
                <StyledSpanForProfileImageDeleteIconButton
                  style={{ borderRadius: '50%', marginTop: '-25px', marginBottom: '5px', scale: '0.8' }}
                >
                  <DeleteIcon
                    onClick={() => onDeleteProfileImage()} />
                </StyledSpanForProfileImageDeleteIconButton>
              </Tooltip>
            </Grid>
          </Grid>
        }

        <TextField
          inputRef={firstNameReg}
          {...firstNameProps}
          autoFocus
          label="First Name *"
          margin='normal'
          fullWidth
          error={!!errors.firstName}
          helperText={errors?.firstName?.message}
        />

        <TextField
          inputRef={lastNameReg}
          {...lastNameProps}
          label="Last Name *"
          margin='normal'
          fullWidth
          error={!!errors.lastName}
          helperText={errors?.lastName?.message}
        />

        <Grid container flexDirection='row'>
          <Grid container size={11} justifySelf='stretch' alignItems='center'>
            <StyledBoxForEmailTextContainer>
              <TextField
                inputRef={emailReg}
                {...emailProps}
                label="Email (change via Edit Button)"
                disabled
                margin='normal'
                fullWidth
                error={!!errors.email}
                helperText={errors?.email?.message}
              />
            </StyledBoxForEmailTextContainer>
          </Grid>
          <Grid container size={1} justifyContent='flex-end' alignItems='center' >
            <Tooltip
              title={TooltipStringAssets.changeEmailAddress}
              arrow
            >
              <span>
                <StyledIconButton
                  onClick={() => navigate(`/updateEmailAddress`)}
                >
                  <EditIcon />
                </StyledIconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>


        <TextField
          inputRef={mobilePhoneReg}
          {...mobilePhoneProps}
          type="tel"
          label="Mobile Phone *"
          margin='normal'
          fullWidth
          error={!!errors.mobilePhone}
          helperText={errors?.mobilePhone?.message}
          // we need a custom onChange handler to normalize (format) the phone number as the user is entering the digits
          onChange={(event) => {
            event.target.value = normalizePhoneNumber(event.target.value)
          }}
        />

      </FormWithActionBar>
    </>

  );
}

export default UserSettingsForm;